import { useTranslation } from 'react-i18next';

import ExamFormService, {
  ExamFormServiceSuccess,
} from '@/components/exam/ExamFormService';
import ExamQuestionsTab from '@/components/exam/ExamQuestionsTab';
import ExamSettingsTab from '@/components/exam/ExamSettingsTab';
import WorkspaceLayout from '@/components/shared/WorkspaceLayout';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Skeleton, Tabs } from '@/components/ui/daisy';
import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';
import useExamWorkspace from '@/data/hook/exam/useExamWorkspace';
import usePedWorkspace from '@/data/hook/ped-content/usePedWorkspace';
import useWorkspaceTabs from '@/data/hook/useWorkspaceTabs';
import { ROUTES } from '@/utils/routes';
import { Cog8ToothIcon } from '@heroicons/react/24/outline';

export enum ExamTabs {
  INFO = 'info',
  QUESTIONS = 'questions',
  SETTINGS = 'settings',
}

export default function ExamWorkspace() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.examWorkspace',
  });
  const {
    exam,
    slug,
    courseLineId,
    moduleId,
    isLoading: isLoadingExam,
  } = useExamWorkspace();
  const { coursePath, isLoading: isLoadingCoursePath } = usePedWorkspace();
  const { navigate } = useCourseTreeNavigate();
  const { tab, setTab } = useWorkspaceTabs<ExamTabs>({
    defaultTab: ExamTabs.INFO,
  });

  const handleSuccess = (response: ExamFormServiceSuccess) => {
    if (response.action !== 'create' || !slug) return;
    navigate(
      ROUTES.ADMIN.COURSE_LINES.EXAM(
        courseLineId,
        moduleId,
        slug,
        response.item.id,
      ),
      { replace: true },
    );
  };

  if (isLoadingExam || isLoadingCoursePath || !coursePath) {
    return <LoadingView />;
  }

  return (
    <WorkspaceLayout
      title={t('title')}
      subtitle={exam?.name || t('newExam')}
      description={exam?.description ?? undefined}
    >
      <Tabs
        variant="box"
        className="w-fit"
        value={tab}
        onChange={value => setTab(value as ExamTabs)}
      >
        <Tabs.Tab value={ExamTabs.INFO}>{t('tabs.information')}</Tabs.Tab>
        <Tabs.Tab value={ExamTabs.QUESTIONS} disabled={!exam}>
          {t('tabs.questions')}
        </Tabs.Tab>
        <Tabs.Tab
          value={ExamTabs.SETTINGS}
          disabled={!exam}
          className="flex gap-2"
        >
          <Cog8ToothIcon className="size-3.5" />
          {t('tabs.settings')}
        </Tabs.Tab>
      </Tabs>
      <ConditionalRenderer condition={tab === ExamTabs.INFO}>
        <ExamFormService
          key={exam?.id ?? 'new'}
          coursePathId={coursePath.id}
          courseLineId={courseLineId}
          onSuccess={handleSuccess}
        />
      </ConditionalRenderer>
      <ConditionalRenderer condition={tab === ExamTabs.QUESTIONS && !!exam}>
        {exam && <ExamQuestionsTab exam={exam} />}
      </ConditionalRenderer>
      <ConditionalRenderer condition={tab === ExamTabs.SETTINGS && !!exam}>
        {exam && <ExamSettingsTab exam={exam} coursePath={coursePath} />}
      </ConditionalRenderer>
    </WorkspaceLayout>
  );
}

const LoadingView = () => {
  return (
    <div className="flex flex-col flex-1 rounded-2xl p-6 gap-6 bg-base-100">
      <div className="flex flex-col gap-5">
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-8 w-40" />
      </div>
      <Skeleton className="h-12 w-96" />
      <Skeleton className="h-96 w-full" />
      <div className="flex justify-end">
        <Skeleton className="h-12 w-32" />
      </div>
    </div>
  );
};
